import React, { useState } from "react";

const ORDER = [
  ["demand", "Demand", 25],
  ["earning_proof", "Proof Others Earn", 20],
  ["monetization", "Monetization", 15],
  ["feasibility", "Solo Feasibility", 15],
  ["competition", "Competition", 15],
  ["differentiation", "Differentiation", 10],
];

// Verdict text long enough to warrant a "more" toggle on mobile.
const LONG_THRESHOLD = 110;

function FootnoteRefs({ nums = [] }) {
  if (!nums || nums.length === 0) return null;
  return (
    <sup className="ml-1 text-[10px] text-[#4ADE80] font-semibold tracking-wider tabular-nums">
      {nums.map((n, i) => (
        <span key={n}>
          {i > 0 ? "," : ""}
          {n}
        </span>
      ))}
    </sup>
  );
}

function DimensionItem({ keyName, label, cap, dimensions, notes, biggestGaps }) {
  const val = Number(dimensions[keyName] ?? 0);
  const pct = Math.max(0, Math.min(100, (val / cap) * 100));
  const tone = pct >= 75 ? "#4ADE80" : pct >= 45 ? "#EAB308" : "#EF4444";
  const noteEntry = notes[keyName];

  let verdict = "";
  let detail = "";
  let footnotes = [];
  if (typeof noteEntry === "string") {
    verdict = noteEntry;
  } else if (noteEntry && typeof noteEntry === "object") {
    verdict = noteEntry.verdict || "";
    detail = noteEntry.detail || "";
    footnotes = noteEntry.footnotes || [];
  }
  const isBiggestGap = biggestGaps.includes(keyName);
  const isLong = verdict.length > LONG_THRESHOLD;
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-testid={`dim-${keyName}`} className="gl-card-lift p-4">
      <div className="flex items-start justify-between gap-3 sm:gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <div className="gl-label">{label}</div>
            {isBiggestGap && (
              <span
                className="text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 font-bold"
                data-testid={`biggest-gap-${keyName}`}
              >
                Biggest Gap
              </span>
            )}
          </div>
          {verdict ? (
            <>
              <div
                className={`text-white font-semibold text-sm sm:text-base leading-snug ${
                  isLong && !expanded ? "line-clamp-2" : ""
                }`}
              >
                {verdict}
                <FootnoteRefs nums={footnotes} />
              </div>
              {isLong ? (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-1.5 text-[11px] text-[#4ADE80] font-semibold uppercase tracking-[0.14em]"
                  data-testid={`dim-${keyName}-toggle`}
                >
                  {expanded ? "Less" : "More"}
                </button>
              ) : null}
              {detail && expanded ? (
                <div className="text-xs text-[#b6c1b8] mt-2 max-w-xl leading-relaxed">
                  {detail}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="font-bold text-xl sm:text-3xl text-white tabular-nums whitespace-nowrap leading-none pt-0.5">
          {val}
          <span className="text-[#6c7770] text-xs sm:text-sm font-light ml-1">
            / {cap}
          </span>
        </div>
      </div>
      <div className="h-2 w-full rounded-full bg-[#1a221c] overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-700 ease-out"
          style={{
            width: `${pct}%`,
            background: tone,
            boxShadow: `0 0 12px ${tone}55`,
          }}
        />
      </div>
    </div>
  );
}

export default function DimensionBars({ dimensions = {}, notes = {}, biggestGaps = [] }) {
  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dimension-breakdown">
      {ORDER.map(([key, label, cap]) => (
        <DimensionItem
          key={key}
          keyName={key}
          label={label}
          cap={cap}
          dimensions={dimensions}
          notes={notes}
          biggestGaps={biggestGaps}
        />
      ))}
    </div>
  );
}
